import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bell, ExternalLink, Filter, ChevronDown, ChevronUp, Check, Image as ImageIcon } from 'lucide-react';
import { useUserNotifications, useMarkNotificationAsRead, useDeleteNotification } from '../../hooks';
import { Card, LoadingSpinner, Button, Badge, EmptyState } from '../../components/common';

/** Util: valida si una URL es http(s) y está bien formada */
function isValidHttpUrl(url?: string | null): boolean {
  if (!url) return false;
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

/** 
 * Componente de imagen con fallback:
 * - Si no hay src (vacío/nulo) => no renderiza nada (NO muestra default).
 * - Si hay src pero la URL es inválida o falla la carga => muestra placeholder por default.
 * - Si la URL es válida y carga => muestra la imagen.
 */
const NotificationImage: React.FC<{ src?: string | null; alt?: string }> = ({ src, alt = 'Imagen de notificación' }) => {
  const [error, setError] = useState(false);

  // Si no hay src (vacío/nulo), no renderizamos nada (cumple: no mostrar icono por default).
  if (!src) {
    return null;
  }

  // Si hay src pero la URL es inválida o ya falló la carga, mostramos placeholder por default.
  if (!isValidHttpUrl(src) || error) {
    return (
      <div className="w-full max-w-full sm:max-w-md h-40 md:h-32 rounded-lg mb-3 md:mb-4 bg-slate-100 flex items-center justify-center text-brinsa-azulOscuro/50">
        <div className="flex items-center gap-2">
          <ImageIcon className="w-5 h-5" />
          <span className="text-xs">La imagen no se pudo cargar</span>
        </div>
      </div>
    );
  }

  // Si es válida, renderiza <img> y si falla, activamos el fallback (placeholder).
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setError(true)}
      className="w-full max-w-full sm:max-w-md h-40 md:h-32 object-cover rounded-lg mb-3 md:mb-4"
    />
  );
};

const NotificationsPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false); // NUEVO: controla panel de filtros en mobile

  const { data: notificationsData, isLoading } = useUserNotifications({
    page,
    limit: 10
  });
  const markAsReadMutation = useMarkNotificationAsRead();
  const deleteNotificationMutation = useDeleteNotification();

  const notifications = notificationsData?.users || [];
  const pagination = notificationsData?.pagination;

  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'unread') return !notification.emailSent;
    if (filter === 'read') return notification.emailSent;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.emailSent).length;

  const markAsRead = async (notificationId: string) => {
    try {
      await markAsReadMutation.mutateAsync(notificationId);
    } catch (error) {
      console.error('Error marcando como leída:', error);
    }
  };

  // NUEVO: acción masiva para marcar todas como leídas (usa el mismo hook)
  const markAllAsRead = async () => {
    try {
      const unread = notifications.filter(n => !n.emailSent);
      await Promise.all(unread.map(n => markAsReadMutation.mutateAsync(n.id)));
    } catch (error) {
      console.error('Error marcando todas como leídas:', error);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      await deleteNotificationMutation.mutateAsync(notificationId);
    } catch (error) {
      console.error('Error eliminando notificación:', error);
    }
  };

  // NUEVO: valida URL antes de abrir para evitar intentos a dominios mal formados
  const openLink = (url: string) => {
    if (isValidHttpUrl(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      console.warn('URL inválida, no se abrirá:', url);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-brinsa-azul mb-1 md:mb-2">
              Notificaciones
            </h1>
            <p className="text-base md:text-lg text-brinsa-azulOscuro/80">
              Mantente al día con las últimas actualizaciones
            </p>
          </div>

          {unreadCount > 0 && (
            <Badge
              variant="error"
              className="px-3 py-1 whitespace-nowrap text-center self-start md:self-auto text-xs md:text-sm"
            >
              {unreadCount} sin leer
            </Badge>
          )}
        </div>
      </motion.div>

      {/* Filtros */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        <Card>
          <div className="p-4">
            {/* Barra compacta (siempre visible). En md+ muestra todo inline, en mobile sólo encabezado + toggle */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 md:gap-4">
                <Filter className="w-5 h-5 text-brinsa-celeste" />
                <span className="text-sm font-medium text-brinsa-azul hidden md:inline">Filtrar:</span>

                {/* Botones de filtros (desktop) */}
                <div className="hidden md:flex md:items-center md:gap-2">
                  {[
                    { key: 'all', label: 'Todas', count: notifications.length },
                    { key: 'unread', label: 'Sin leer', count: unreadCount },
                    { key: 'read', label: 'Leídas', count: notifications.length - unreadCount }
                  ].map(({ key, label, count }) => (
                    <button
                      key={key}
                      onClick={() => setFilter(key as any)}
                      className={`px-3 py-1 rounded-full text-sm transition-colors border border-transparent ${
                        filter === key
                          ? 'bg-brinsa-azul/10 text-brinsa-azul font-medium'
                          : 'text-brinsa-azulOscuro hover:bg-brinsa-celeste/10'
                      }`}
                    >
                      {label} ({count})
                    </button>
                  ))}
                </div>
              </div>

              {/* Botón "Marcar todas" visible en desktop; en mobile va dentro del panel */}
              <div className="hidden md:block">
                <Button size="sm" variant="outline" onClick={markAllAsRead} disabled={markAsReadMutation.isPending}>
                  {markAsReadMutation.isPending ? 'Marcando…' : 'Marcar todas como leídas'}
                </Button>
              </div>

              {/* Toggle de filtros (mobile) */}
              <button
                className="md:hidden inline-flex items-center gap-1 text-sm font-medium text-brinsa-azul"
                onClick={() => setMobileFiltersOpen(v => !v)}
              >
                {mobileFiltersOpen ? 'Ocultar filtros' : 'Filtrar'}
                {mobileFiltersOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>

            {/* Panel colapsable de filtros (mobile) */}
            {mobileFiltersOpen && (
              <div className="mt-4 md:hidden">
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: 'all', label: 'Todas', count: notifications.length },
                    { key: 'unread', label: 'Sin leer', count: unreadCount },
                    { key: 'read', label: 'Leídas', count: notifications.length - unreadCount }
                  ].map(({ key, label, count }) => (
                    <button
                      key={key}
                      onClick={() => setFilter(key as any)}
                      className={`px-3 py-1 rounded-full text-sm transition-colors border ${
                        filter === key
                          ? 'bg-brinsa-azul text-white border-brinsa-azul'
                          : 'text-brinsa-azulOscuro/90 border-slate-200 hover:bg-brinsa-celeste/10'
                      }`}
                    >
                      {label} ({count})
                    </button>
                  ))}
                </div>

                <div className="mt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full"
                    onClick={markAllAsRead}
                    disabled={markAsReadMutation.isPending}
                  >
                    {markAsReadMutation.isPending ? 'Marcando…' : 'Marcar todas como leídas'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Card>
      </motion.div>

      {/* Lista de notificaciones */}
      {filteredNotifications.length > 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="space-y-4"
        >
          {filteredNotifications.map((notification, index) => (
            <motion.div
              key={notification.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
            >
              <Card
                className={`hover:shadow-md transition-shadow ${
                  !notification.emailSent ? 'border-l-4 border-l-brinsa-azulClaro' : ''
                }`}
              >
                <div className="p-4 md:p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 md:space-x-4 flex-1">
                      {/* Icono */}
                      <div
                        className={`w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                          !notification.emailSent
                            ? 'bg-brinsa-celeste/15 text-brinsa-azul'
                            : 'bg-slate-100 text-brinsa-azulOscuro'
                        }`}
                      >
                        <Bell className="w-4 h-4 md:w-5 md:h-5" />
                      </div>

                      {/* Contenido */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 mb-2">
                          <h3
                            className={`text-base md:text-lg font-semibold truncate ${
                              !notification.emailSent ? 'text-brinsa-azul' : 'text-brinsa-azulOscuro'
                            }`}
                            title={notification.title}
                          >
                            {notification.title}
                          </h3>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {!notification.emailSent && (
                              <Badge variant="info" size="sm" className="bg-brinsa-celeste/15 text-brinsa-celeste">
                                Nuevo
                              </Badge>
                            )}
                            <span className="text-xs text-brinsa-azulOscuro/60 whitespace-nowrap">
                              {notification.sentAt ? new Date(notification.sentAt).toLocaleDateString('es-ES') : ''}
                            </span>
                          </div>
                        </div>

                        <p
                          className={`mb-3 md:mb-4 text-sm md:text-base break-words ${
                            !notification.emailSent ? 'text-brinsa-azulOscuro' : 'text-brinsa-azulOscuro/80'
                          }`}
                        >
                          {notification.body}
                        </p>

                        {/* Imagen si está disponible (con fallback si falla o es inválida) */}
                        {notification.imageUrl ? (
                          <NotificationImage src={notification.imageUrl} />
                        ) : (
                          <NotificationImage src={null} />
                        )}

                        {/* Botón de acción si está disponible y la URL es válida */}
                        {notification.actionButtonText && notification.actionButtonUrl && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openLink(notification.actionButtonUrl!)}
                            leftIcon={<ExternalLink className="w-4 h-4" />}
                            className="mb-3 md:mb-4 w-full sm:w-auto"
                            disabled={!isValidHttpUrl(notification.actionButtonUrl!)}
                          >
                            {notification.actionButtonText}
                          </Button>
                        )}

                        {/* Acciones */}
                        <div className="flex flex-wrap items-center gap-3">
                          {!notification.emailSent && (
                            <button
                              onClick={() => markAsRead(notification.id)}
                              disabled={markAsReadMutation.isPending}
                              className="text-sm text-brinsa-azul hover:text-brinsa-azulOscuro font-medium inline-flex items-center"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              {markAsReadMutation.isPending ? 'Marcando...' : 'Marcar como leída'}
                            </button>
                          )}
                          {/* <button
                            onClick={() => deleteNotification(notification.id)}
                            disabled={deleteNotificationMutation.isPending}
                            className="text-sm text-brinsa-rojo hover:opacity-80 font-medium"
                          >
                            <Trash2 className="w-4 h-4 inline mr-1" />
                            {deleteNotificationMutation.isPending ? 'Eliminando...' : 'Eliminar'}
                          </button> */}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <EmptyState
            icon={<Bell className="w-16 h-16 text-brinsa-celeste" />}
            title={filter === 'all' ? 'No tienes notificaciones' : `No tienes notificaciones ${filter === 'unread' ? 'sin leer' : 'leídas'}`}
            description="Las notificaciones aparecerán aquí cuando haya actualizaciones importantes."
          />
        </motion.div>
      )}

      {/* Paginación */}
      {pagination && pagination.totalPages > 1 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Card>
            <div className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <span className="text-sm text-brinsa-azulOscuro/80">
                  Página {pagination.page} de {pagination.totalPages}
                </span>

                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="w-full sm:w-auto"
                  >
                    Anterior
                  </Button>

                  <Button
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={page === pagination.totalPages}
                    className="w-full sm:w-auto"
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      )}
    </div>
  );
};

export default NotificationsPage;
